import Dexie, { type Table } from "dexie";
import type { AnalysisResult } from "../engine/types";
import type { RecoveryReview } from "../engine/review";
import type { RecoveryDecision } from "../engine/decisions";
import type { PostedTicketRecord, TrelloTarget } from "../features/trello/trello-ticket";
import type { ProfileOverride, SourceProfileDelta } from "../profiles/schema";
import type { SourceMeasurement } from "../engine/profile-inference";

/**
 * Version of the persisted analysis/review shape, folded into the analysis cache
 * key. Bump it whenever `AnalysisResult` or `RecoveryReview` gains or changes a
 * field: entries written under the old shape then miss the cache and are rebuilt,
 * instead of being served with fields the current code assumes exist.
 */
export const ANALYSIS_CACHE_SCHEMA_VERSION = 3;

export type SavedAnalysis = {
  analysisKey: string;
  createdAt: string;
  result: AnalysisResult;
  /**
   * Recovery review for the same run. Stored alongside rather than separately so a
   * cache hit can never serve an analysis with a review from a different policy.
   * The analysis key includes the source profile id and version, so approving a
   * field invalidates the cache rather than silently reusing the old outcome.
   */
  review?: RecoveryReview | null;
  /**
   * Pinned by the person, so routine eviction leaves it alone.
   *
   * Pinning is capped (`ANALYSIS_CACHE_MAX_PINNED`) rather than unlimited: an
   * exemption with no ceiling is a cache with no bound, and the first symptom
   * of that is every later write failing on quota. A quota failure still clears
   * everything, pins included — at that point the choice is between losing
   * pinned runs and caching nothing at all.
   */
  keep?: boolean;
};

/**
 * One recorded decision. The store is append-only: a change of mind adds a row,
 * and the latest row for a cell wins. Rows are never updated or deleted, because
 * the history is the audit trail.
 */
export type SavedDecision = RecoveryDecision & {
  /**
   * The run's analysis key: the hash of its inputs and its policy. Decisions
   * recorded against the same pair under the same policy therefore belong to
   * the same run even if it is analysed again after the cache evicted it.
   *
   * Until schema v9 this column held `review.generatedAt` instead — a run
   * timestamp, so re-analysing a pair orphaned its decisions. The v9 upgrade
   * rewrites what it can (see `migrateDecisionScopes`).
   */
  analysisKey: string;
  /**
   * Set by the v9 upgrade on a row it rewrote: what the row was scoped by
   * before. Kept because the decision log is an audit trail — a migration that
   * silently re-labels evidence should leave the old label behind.
   */
  legacyScope?: string;
};

/**
 * Trello posting attempts, append-only. Carries no token, no API key, and no card
 * body — only a hash of what was sent.
 */
export type SavedPostedTicket = PostedTicketRecord;

/**
 * Non-secret Trello target. The token is deliberately absent: it lives in memory for
 * the session, or in sessionStorage only if the user opts in. See
 * docs/design/trello-integration.md section 7.
 */
export type SavedTrelloTarget = TrelloTarget;

/**
 * A local per-source policy override: a delta applied over the repo profile at
 * resolution time (see src/profiles/resolve.ts). One row per profile. The
 * `reason` is required — an override is a policy decision and must be
 * auditable (AGENTS.md rule 7); the resolved policy hash changes with it, so
 * every artifact records that it ran under the override.
 */
export type SavedProfileOverride = ProfileOverride;

/**
 * A source profile measured from an uploaded pair and stored locally, for a
 * source no committed profile matches.
 *
 * Deliberately a separate table from `profileOverrides`, because the two are
 * different things. An override AMENDS committed policy and is keyed by the
 * repo profile it amends; a draft has no repo profile at all and merges
 * straight onto the base. A draft is also never itself overridable — editing
 * one means measuring again — so it stores the whole delta rather than a
 * delta-on-a-delta.
 *
 * The measurement is kept alongside the delta so the evidence a person saw when
 * they saved it can be shown again, and so the notes can be regenerated without
 * re-reading the files.
 */
export type SavedDraftProfile = {
  /** The proposed profile id. Primary key. */
  draftId: string;
  delta: SourceProfileDelta;
  measurement: SourceMeasurement;
  /** ISO-8601. Becomes the draft stamp's measuredAt. */
  createdAt: string;
  baselineFileName: string;
  latestFileName: string;
};

/**
 * A receipt that an artifact was handed to the browser for download.
 *
 * Append-only, one row per download, because "exported" is not a single moment:
 * a person may take the recovered JSON now and the audit zip later, and a
 * re-export after more decisions is a different hand-back from the first.
 *
 * It records that a download STARTED. Whether the browser then wrote the file,
 * where it put it, or whether the person kept it, is outside what a page can
 * observe — so the flow says "exported" and never "delivered".
 */
export type SavedExport = {
  /** Auto-incremented by Dexie. */
  id?: number;
  /** The run the artifact came from. */
  analysisKey: string;
  fileName: string;
  /** ISO-8601. */
  at: string;
};

class DriftDatabase extends Dexie {
  analyses!: Table<SavedAnalysis, string>;
  decisions!: Table<SavedDecision, string>;
  postedTickets!: Table<SavedPostedTicket, string>;
  trelloTarget!: Table<SavedTrelloTarget, string>;
  profileOverrides!: Table<SavedProfileOverride, string>;
  draftProfiles!: Table<SavedDraftProfile, string>;
  exports!: Table<SavedExport, number>;

  constructor() {
    super("json-data-drift-analyzer");
    this.version(1).stores({
      analyses: "analysisKey, createdAt",
      profiles: "id",
      textDiffs: "id"
    });
    this.version(2).stores({
      analyses: "analysisKey, createdAt",
      textDiffs: "id"
    });
    // Decisions are keyed by their own id and indexed by run, so a run's log can be
    // read back without scanning, and appending never overwrites an earlier entry.
    this.version(3).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      textDiffs: "id"
    });
    // postedTickets is indexed by fingerprint so the duplicate check is a lookup, not
    // a scan. trelloTarget holds one row and never holds the token.
    this.version(4).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
      trelloTarget: "id",
      textDiffs: "id"
    });
    // Dexie only deletes an object store when a version names it null; the v2
    // schema merely omitted `profiles`, so databases created at v1 still carry
    // the orphaned store. This drops it for real.
    this.version(5).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
      trelloTarget: "id",
      textDiffs: "id",
      profiles: null
    });
    // Deliberately NOT named `profiles` (the store v5 deletes): this table
    // stores DELTAS over repo profiles, never whole profiles — the repo files
    // stay the source of truth and an override is a local amendment to them.
    this.version(6).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
      trelloTarget: "id",
      textDiffs: "id",
      profileOverrides: "profileId, updatedAt"
    });
    // Drafts are whole measured profiles for sources no committed profile
    // matches — not amendments to one, which is why they do not live in
    // profileOverrides.
    this.version(7).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
      trelloTarget: "id",
      textDiffs: "id",
      profileOverrides: "profileId, updatedAt",
      draftProfiles: "draftId, createdAt"
    });
    // Export receipts. Downloading an artifact was previously invisible to the
    // app the moment the browser took the file, so nothing could tell a run
    // that had been handed back from one that had not — which the guided flow
    // needs to say, and which the run history will need too.
    this.version(8).stores({
      analyses: "analysisKey, createdAt",
      decisions: "id, analysisKey, timestamp",
      postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
      trelloTarget: "id",
      textDiffs: "id",
      profileOverrides: "profileId, updatedAt",
      draftProfiles: "draftId, createdAt",
      exports: "++id, analysisKey, at"
    });
    // Two changes, both about naming things by what they are.
    //
    // `textDiffs` is dropped for real (naming it null is the only way Dexie
    // deletes a store): nothing has written to it since text diffing moved into
    // the record graph, and an empty store that looks like a cache invites a
    // future reader to trust it.
    //
    // `decisions.analysisKey` becomes the actual analysis key. It held
    // `review.generatedAt` — a run timestamp — so re-analysing the same pair
    // under the same policy produced a new scope and orphaned every decision
    // recorded against it, and nothing in the types told the two strings apart.
    // The upgrade rewrites the rows it can attribute and leaves the rest
    // untouched: this is an audit trail, so nothing is deleted and every
    // rewritten row keeps its old scope in `legacyScope`.
    this.version(9)
      .stores({
        analyses: "analysisKey, createdAt",
        decisions: "id, analysisKey, timestamp",
        postedTickets: "id, runFingerprint, analysisKey, attemptedAt",
        trelloTarget: "id",
        textDiffs: null,
        profileOverrides: "profileId, updatedAt",
        draftProfiles: "draftId, createdAt",
        exports: "++id, analysisKey, at"
      })
      .upgrade((tx) =>
        migrateDecisionScopes({
          // Streamed rather than read into an array: a cached analysis is
          // several megabytes and there can be twenty of them.
          eachAnalysis: (visit) => tx.table("analyses").each(visit),
          modifyDecisions: (change) => tx.table("decisions").toCollection().modify(change)
        })
      );
  }
}

/** The two table operations the decision-scope migration needs. */
export type DecisionScopeMigrationTables = {
  eachAnalysis: (
    visit: (row: { analysisKey?: string; review?: { generatedAt?: string } | null }) => void
  ) => PromiseLike<unknown>;
  modifyDecisions: (change: (row: SavedDecision) => void) => PromiseLike<unknown>;
};

/**
 * Re-scope the decision log from `review.generatedAt` to the analysis key.
 *
 * Pure of Dexie so the interesting part is testable: which rows can be
 * attributed, and what happens to the ones that cannot. A row whose run is no
 * longer in the analyses cache keeps the scope it has — it was already orphaned
 * by the eviction, and deleting evidence to tidy a column is not a trade this
 * app makes.
 *
 * Written as a promise chain rather than with async/await: Dexie tracks the
 * transaction in a zone that a native `await` can leave, and its documentation
 * asks for chains inside `upgrade`. Both forms were tried against a real v8
 * database created by v1.14.0 and both completed, so this is following the
 * documented contract rather than fixing an observed failure — worth saying,
 * because the failure it guards against would be a database that never finishes
 * opening, and nobody should have to rediscover why the shape matters.
 */
export function migrateDecisionScopes(tables: DecisionScopeMigrationTables): PromiseLike<void> {
  const keyByGeneratedAt = new Map<string, string>();
  return tables
    .eachAnalysis((row) => {
      const generatedAt = row.review?.generatedAt;
      if (typeof generatedAt === "string" && typeof row.analysisKey === "string") {
        keyByGeneratedAt.set(generatedAt, row.analysisKey);
      }
    })
    .then(() => {
      if (keyByGeneratedAt.size === 0) return undefined;
      return tables.modifyDecisions((row) => {
        const real = keyByGeneratedAt.get(row.analysisKey);
        // Already migrated, or never scoped by a timestamp this cache knows.
        if (real === undefined || real === row.analysisKey) return;
        row.legacyScope = row.analysisKey;
        row.analysisKey = real;
      });
    })
    .then(() => undefined);
}

export const db = new DriftDatabase();

/**
 * Cap on cached analyses. Each SavedAnalysis holds the full record graph, the
 * serialized search index, and every finding — several MB apiece. Without a cap
 * the cache only ever grows, and once browser quota is hit every later `put`
 * fails forever. Twenty entries comfortably covers a working week of runs while
 * staying far from any realistic quota.
 */
export const ANALYSIS_CACHE_MAX_ENTRIES = 20;

/**
 * How many runs may be pinned against eviction at once.
 *
 * Well below the cache cap, so pinning can never starve it: with every slot
 * pinned, a new run would evict nothing and the cache would grow until the
 * browser refused the write.
 */
export const ANALYSIS_CACHE_MAX_PINNED = 5;

function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const name = (error as { name?: string }).name;
  // Dexie wraps the DOMException; the inner error keeps the original name.
  const inner = (error as { inner?: { name?: string } }).inner;
  return name === "QuotaExceededError" || inner?.name === "QuotaExceededError";
}

/** The slice of the analyses table the bounded put needs; injectable for tests. */
export type AnalysesTableLike = {
  put: (entry: SavedAnalysis) => Promise<unknown>;
  get: (key: string) => Promise<SavedAnalysis | undefined>;
  count: () => Promise<number>;
  bulkDelete: (keys: string[]) => Promise<unknown>;
  toArray: () => Promise<SavedAnalysis[]>;
  orderBy: (index: "createdAt") => { limit: (n: number) => { primaryKeys: () => Promise<string[]> } };
};

/**
 * Delete the oldest cached analyses until at most `keep` remain.
 *
 * Pinned runs are skipped. That means reading the rows rather than taking the
 * oldest primary keys off the index — the index cannot express "oldest,
 * excluding pinned" — which is a table read of a bounded table, on a path that
 * already just wrote several megabytes.
 *
 * `keep` of zero is the quota-recovery path and clears everything, pins
 * included: when the browser has refused a write there is no version of
 * "honour the pins" that also leaves the cache usable.
 */
async function evictOldestAnalyses(table: AnalysesTableLike, keep: number): Promise<void> {
  const count = await table.count();
  if (count - keep <= 0) {
    return;
  }
  if (keep === 0) {
    const allKeys = await table.orderBy("createdAt").limit(count).primaryKeys();
    await table.bulkDelete(allKeys);
    return;
  }

  const rows = await table.toArray();
  const evictable = rows
    .filter((row) => row.keep !== true)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  // Pinned rows still occupy slots, so the number to drop is measured against
  // the whole cache and taken from the evictable ones.
  const excess = Math.min(rows.length - keep, evictable.length);
  if (excess <= 0) return;
  await table.bulkDelete(evictable.slice(0, excess).map((row) => row.analysisKey));
}

/**
 * Pin or unpin a run against routine eviction.
 *
 * Refuses past the cap rather than silently ignoring the request, and says so,
 * because a pin a person believes is holding a run that is in fact evictable is
 * worse than no pinning at all.
 */
export async function setAnalysisPinned(
  analysisKey: string,
  keep: boolean,
  table: AnalysesTableLike = db.analyses
): Promise<{ ok: true } | { ok: false; reason: string }> {
  const row = await table.get(analysisKey).catch(() => undefined);
  if (row === undefined) {
    return { ok: false, reason: "That run is no longer in this browser's cache." };
  }
  if (keep) {
    const rows = await table.toArray().catch(() => [] as SavedAnalysis[]);
    const pinned = rows.filter((entry) => entry.keep === true && entry.analysisKey !== analysisKey).length;
    if (pinned >= ANALYSIS_CACHE_MAX_PINNED) {
      return {
        ok: false,
        reason: `At most ${ANALYSIS_CACHE_MAX_PINNED} runs can be kept. Unpin one first.`
      };
    }
  }
  await table.put({ ...row, keep });
  return { ok: true };
}

/**
 * Cache an analysis, keeping the cache bounded.
 *
 * After every write the oldest entries beyond ANALYSIS_CACHE_MAX_ENTRIES are
 * evicted. If the write itself hits browser quota, the whole cache is evicted
 * and the write retried once — the cache is an optimization, and a full cache
 * must never make caching fail forever. A second failure propagates to the
 * caller, which already degrades to session-only with a toast.
 */
export async function putAnalysisBounded(entry: SavedAnalysis, table: AnalysesTableLike = db.analyses): Promise<void> {
  try {
    await table.put(entry);
  } catch (error) {
    if (!isQuotaError(error)) {
      throw error;
    }
    await evictOldestAnalyses(table, 0);
    await table.put(entry);
  }
  await evictOldestAnalyses(table, ANALYSIS_CACHE_MAX_ENTRIES);
}

/**
 * Every cached analysis, for attributing runs to sources. Degrades to an empty
 * list: the fleet view then shows no run history, never a wrong one.
 *
 * Reads the whole table on purpose — it is bounded to
 * ANALYSIS_CACHE_MAX_ENTRIES rows, and the profile a run used lives inside the
 * review rather than in an index, so there is nothing narrower to query on.
 */
export async function listAnalyses(
  table: Pick<Table<SavedAnalysis, string>, "toArray"> = db.analyses
): Promise<SavedAnalysis[]> {
  return await table.toArray().catch(() => [] as SavedAnalysis[]);
}

// ---------------------------------------------------------------------------
// Export receipts
// ---------------------------------------------------------------------------

/** The slice of the exports table the helpers need; injectable for tests. */
export type ExportsTableLike = {
  add: (row: SavedExport) => Promise<unknown>;
  toArray: () => Promise<SavedExport[]>;
};

/**
 * Record that an artifact download started.
 *
 * Failures are swallowed on purpose, unlike a decision write: this is a
 * receipt, not policy. The file has already been handed to the browser by the
 * time this runs, so a failing IndexedDB must not surface an error about an
 * export that in fact succeeded. The cost is a guided-flow step that stays
 * unticked, which understates progress rather than overstating it.
 */
export async function recordExport(row: SavedExport, table: ExportsTableLike = db.exports): Promise<void> {
  await table.add(row).catch(() => undefined);
}

/** Every export receipt, oldest first. Degrades to an empty list. */
export async function listExports(table: ExportsTableLike = db.exports): Promise<SavedExport[]> {
  const rows = await table.toArray().catch(() => [] as SavedExport[]);
  return [...rows].sort((left, right) => left.at.localeCompare(right.at));
}

// ---------------------------------------------------------------------------
// Profile overrides
// ---------------------------------------------------------------------------

/** The slice of the overrides table the helpers need; injectable for tests. */
export type ProfileOverridesTableLike = {
  get: (key: string) => Promise<SavedProfileOverride | undefined>;
  put: (row: SavedProfileOverride) => Promise<unknown>;
  delete: (key: string) => Promise<unknown>;
  toArray: () => Promise<SavedProfileOverride[]>;
};

/**
 * Read a profile's local override. Degrades to "no override" on a failing
 * IndexedDB (private browsing, corrupted DB): analysis must still run — it
 * just runs under the unmodified repo policy, which the resolved policy hash
 * records faithfully either way.
 */
export async function getProfileOverride(
  profileId: string,
  table: ProfileOverridesTableLike = db.profileOverrides
): Promise<SavedProfileOverride | null> {
  const row = await table.get(profileId).catch(() => undefined);
  return row ?? null;
}

/**
 * Save (create or replace) a profile's local override. Write failures
 * propagate: unlike a cache miss, a policy edit the user believes was saved
 * but was not is a silent policy divergence.
 */
export async function putProfileOverride(
  override: SavedProfileOverride,
  table: ProfileOverridesTableLike = db.profileOverrides
): Promise<void> {
  await table.put(override);
}

/**
 * Every stored override. Degrades to an empty list on a failing IndexedDB, like
 * `getProfileOverride`: the fleet view then reports no local amendments, which
 * is the same thing it reports when there are none. It is a read for display —
 * no policy is resolved from it.
 */
export async function listProfileOverrides(
  table: ProfileOverridesTableLike = db.profileOverrides
): Promise<SavedProfileOverride[]> {
  return await table.toArray().catch(() => [] as SavedProfileOverride[]);
}

/** Remove a profile's local override, restoring the repo policy. */
export async function deleteProfileOverride(
  profileId: string,
  table: ProfileOverridesTableLike = db.profileOverrides
): Promise<void> {
  await table.delete(profileId);
}

// ---------------------------------------------------------------------------
// Draft profiles
// ---------------------------------------------------------------------------

/** The slice of the drafts table the helpers need; injectable for tests. */
export type DraftProfilesTableLike = {
  get: (key: string) => Promise<SavedDraftProfile | undefined>;
  put: (row: SavedDraftProfile) => Promise<unknown>;
  delete: (key: string) => Promise<unknown>;
  toArray: () => Promise<SavedDraftProfile[]>;
};

/**
 * Read one draft. Degrades to "no draft" on a failing IndexedDB, matching
 * `getProfileOverride`: a missing draft means the picker offers committed
 * profiles only, which is a worse experience but never a wrong policy.
 */
export async function getDraftProfile(
  draftId: string,
  table: DraftProfilesTableLike = db.draftProfiles
): Promise<SavedDraftProfile | null> {
  const row = await table.get(draftId).catch(() => undefined);
  return row ?? null;
}

/** Every stored draft, oldest first. Degrades to an empty list. */
export async function listDraftProfiles(
  table: DraftProfilesTableLike = db.draftProfiles
): Promise<SavedDraftProfile[]> {
  const rows = await table.toArray().catch(() => [] as SavedDraftProfile[]);
  return [...rows].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

/**
 * Save a draft. Write failures propagate rather than being swallowed: a person
 * who believes they saved a measured profile, and did not, would re-run the
 * analysis under some other source's policy without knowing.
 */
export async function putDraftProfile(
  draft: SavedDraftProfile,
  table: DraftProfilesTableLike = db.draftProfiles
): Promise<void> {
  await table.put(draft);
}

/** Discard a draft. */
export async function deleteDraftProfile(
  draftId: string,
  table: DraftProfilesTableLike = db.draftProfiles
): Promise<void> {
  await table.delete(draftId);
}
